import axios from 'axios';
import store from '@speechki-frontend/core/store/store';

import { refreshGuestAccessToken } from '@speechki-frontend/core/auth/services/resfresh';
import { getGuestAccessToken, getAuthToken } from '@speechki-frontend/core/auth/utils';
import { checkEditorAvailability } from '@speechki-frontend/core/auth/services/availability';

const instances = {
    // 'https://coredev.books.speechki.org': *instance*
};

// Why a guest session refresh failed. Only AUTH means the session is dead.
export const GUEST_REFRESH_FAILURE = {
    AUTH: 'auth', // Ianus rejected the refresh token, or hermes rejected the new token
    UNAVAILABLE: 'unavailable', // tokens are fine, the editor is closed at this order stage
    TECHNICAL: 'technical' // 5xx / network: keep the session, the user can retry
};

function refreshFailure(reason, error) {
    return Object.assign(new Error(`Guest session refresh failed: ${reason}`), {
        reason,
        status: error?.response?.status
    });
}

// Refresh tokens are single-use: parallel failing requests must share ONE refresh.
let guestRefreshPromise;

function refreshGuestSession() {
    if (guestRefreshPromise) return guestRefreshPromise;

    guestRefreshPromise = refreshGuestAccessToken()
        .catch(error => {
            // Ianus answers 4xx for a used/unknown refresh token
            const status = error?.response?.status;
            const reason =
                status >= 400 && status < 500
                    ? GUEST_REFRESH_FAILURE.AUTH
                    : GUEST_REFRESH_FAILURE.TECHNICAL;
            throw refreshFailure(reason, error);
        })
        .then(({ data: { token, refresh_token } }) => {
            sessionStorage.setItem('token', token);
            sessionStorage.setItem('refresh_token', refresh_token);
            sessionStorage.setItem('guest_access', true);
            return store
                .dispatch('guestAccess/createGuestAccessState', {
                    token,
                    refreshToken: refresh_token
                })
                .then(() => token);
        })
        .then(token =>
            checkEditorAvailability().then(
                availability => {
                    if (!availability?.is_available) {
                        throw refreshFailure(GUEST_REFRESH_FAILURE.UNAVAILABLE);
                    }
                    return token;
                },
                error => {
                    const status = error?.response?.status;
                    // hermes: 401 = editor closed at this stage, 403 = wrong token
                    if (status === 401) {
                        throw refreshFailure(GUEST_REFRESH_FAILURE.UNAVAILABLE, error);
                    }
                    const reason =
                        status === 403
                            ? GUEST_REFRESH_FAILURE.AUTH
                            : GUEST_REFRESH_FAILURE.TECHNICAL;
                    throw refreshFailure(reason, error);
                }
            )
        )
        .finally(() => {
            guestRefreshPromise = undefined;
        });

    return guestRefreshPromise;
}

function getLocale() {
    return localStorage.getItem('locale') || 'eng';
}

/**
 * Creates axios instance for this baseurl or returns already created instance
 * @param {string} baseURL
 * @param {*} extraparams
 * @returns
 */
export function createAxiosInstance(baseURL, extraParams = { enableGuestAccess: false }) {
    const key = `${baseURL}_enableGuestAccess:${extraParams.enableGuestAccess}`;

    if (instances[key]) {
        return instances[key];
    }

    const _axios = axios.create({
        baseURL
    });

    instances[baseURL] = _axios;

    function isGuestAccess() {
        return extraParams.enableGuestAccess && !!sessionStorage.getItem('guest_access');
    }

    _axios.defaults.headers.common['Accept-Language'] = getLocale().slice(0, 2);

    if (extraParams.withouthAuth !== true) {
        _axios.interceptors.request.use(
            function(config) {
                if (isGuestAccess()) {
                    config.headers.Authorization = 'Guest ' + getGuestAccessToken();
                    return config;
                }

                const token = getAuthToken();

                if (token) {
                    config.headers['Authorization'] = 'JWT ' + token;
                }

                return config;
            },
            function(error) {
                return Promise.reject(error);
            }
        );
    }

    _axios.interceptors.response.use(
        function(response) {
            console.log(response.config.url, response.data);
            return response.data;
        },
        function(error) {
            const { response } = error;
            if (response) {
                const isHermes = error.config.baseURL === process.env.VUE_APP_HERMES_API_URL;

                // Hermes answers 401 only from is_editor_available: the editor is
                // closed at this order stage. The token is fine (a wrong token is
                // 403 there), so no refresh and no logout.
                if (isHermes && response.status === 401 && isGuestAccess()) {
                    return Promise.reject({ ...response.data, status: response.status });
                }

                // Special behavior for hermes 403. Core 403 is NOT handled here:
                // for guests it usually means the role lacks one action
                // (e.g. chars_info), not a stale token.
                if (response.status === 401 || (isHermes && response.status === 403)) {
                    if (isGuestAccess()) {
                        const originalRequest = error.config;
                        const rejection = () =>
                            Promise.reject({ ...response.data, status: response.status });

                        const failGuestAccess = () => {
                            // parallel requests share one failed refresh: end the session once
                            if (!sessionStorage.getItem('guest_access')) return rejection();

                            sessionStorage.clear();
                            store.commit('CLEAR_USER_DATA');
                            store.commit('REFRESH_TOKEN_ERROR');
                            return rejection();
                        };

                        const onRefreshFailure = failure => {
                            if (failure?.reason === GUEST_REFRESH_FAILURE.UNAVAILABLE) {
                                // the session is valid, the order stage closes the editor:
                                // report 401 whatever the original status was, so callers
                                // (AuthProvider) show "access denied", not "token expired"
                                return Promise.reject({ is_available: false, status: 401 });
                            }

                            if (failure?.reason === GUEST_REFRESH_FAILURE.TECHNICAL) {
                                // keep the session; report the real 5xx/network failure
                                return Promise.reject({
                                    detail: 'Guest session refresh failed',
                                    status: failure.status
                                });
                            }

                            return failGuestAccess();
                        };

                        if (originalRequest.isGuestAccessRetry) {
                            return failGuestAccess();
                        }

                        originalRequest.isGuestAccessRetry = true;

                        const sentAuthorization = originalRequest.headers?.Authorization;
                        const currentToken = getGuestAccessToken();

                        // The token was already refreshed by a parallel request
                        // while this one was in flight: just retry with it.
                        if (
                            !guestRefreshPromise &&
                            currentToken &&
                            sentAuthorization &&
                            sentAuthorization !== 'Guest ' + currentToken
                        ) {
                            originalRequest.headers.Authorization = 'Guest ' + currentToken;
                            return _axios(originalRequest);
                        }

                        return refreshGuestSession().then(
                            token => {
                                originalRequest.headers.Authorization = 'Guest ' + token;
                                return _axios(originalRequest);
                            },
                            onRefreshFailure
                        );
                    }

                    store.commit('CLEAR_USER_DATA');
                    return Promise.reject({
                        ...error.response.data,
                        status: error.response.status
                    });
                }

                if (response.status >= 500) {
                    store.dispatch('pushToast', {
                        id: Date.now(),
                        type: 'error',
                        title: `${response.status} ${response.statusText}`,
                        text: 'Repeat the action again, or reload the page.'
                    });

                    if (extraParams.rejectOn5xx && isGuestAccess()) {
                        return Promise.reject({ ...response.data, status: response.status });
                    }
                    return;
                }

                if (response.data) {
                    return Promise.reject({
                        ...response.data,
                        status: response.status
                    });
                }
                return Promise.reject(error.response);
            } else {
                window.location.href = '/unavailable';
                return Promise.reject();
            }
        }
    );

    return _axios;
}
